import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PiTrendDown, PiTrendUp } from 'react-icons/pi';
import { useFinancialPrivacy } from '../../FinancialPrivacyContext';
import { formatCents } from '../../domain/finance';
import { buildTemporalAxisLabels } from './temporalAxis';
import { DEFAULT_FLOW_SERIES } from './seriesVisibility';
import { useChartLayout } from './useChartLayout';
import { buildFlowGeometry } from './flowGeometry';
import { FLOW_COLORS, useSeriesPaint } from './useSeriesPaint';

const SERIES = {
    income: { label: 'Receitas', Icon: PiTrendUp },
    expense: { label: 'Despesas', Icon: PiTrendDown },
};

const getAlignment = (index, total) => {
    if (total <= 2) return index === 0 ? 'start' : 'end';
    if (index <= 1) return 'start';
    if (index >= total - 2) return 'end';
    return 'center';
};

const FinancialFlowChart = ({ data, visibleSeries = DEFAULT_FLOW_SERIES, accountId, periodKey, debug = process.env.NODE_ENV === 'development' }) => {
    const { valuesVisible } = useFinancialPrivacy();
    const containerRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(null);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const selectedIndex = hoveredIndex ?? activeIndex;
    const hasSeries = visibleSeries.length > 0;
    const comparesTypes = visibleSeries.includes('income') && visibleSeries.includes('expense');

    useEffect(() => {
        setActiveIndex(null);
        setHoveredIndex(null);
    }, [data, visibleSeries, accountId, periodKey]);
    const layout = useChartLayout(containerRef, { accountId, periodKey, data, visibleSeries });
    // A finite viewBox always paints; 720 is only a label-density fallback, not
    // evidence that a zero-sized wrapper has finished laying out.
    const containerWidth = layout.width || 720;
    const axisLabels = useMemo(
        () => buildTemporalAxisLabels(data, containerWidth),
        [data, containerWidth]
    );

    const displayData = useMemo(
        () => valuesVisible
            ? data
            : data.map((item) => ({ ...item, income: 0, expense: 0 })),
        [data, valuesVisible]
    );

    // Rebuild the small SVG geometry on every render, including context/layout
    // changes with an identical dataset. Never retain paths from a former render.
    const geometry = buildFlowGeometry(displayData, visibleSeries);
    const { width, height, padding, chartHeight, getX, getY } = geometry;
    const canInteract = hasSeries && geometry.valid;

    useSeriesPaint(containerRef, { geometry, layout, accountId, periodKey, visibleSeries, valuesVisible, debug });

    const activeItem = !canInteract || selectedIndex === null
        ? null : displayData[selectedIndex];

    const activeX = activeItem ? getX(selectedIndex) : null;

    const activePercent =
        activeX === null
            ? 50
            : (activeX / width) * 100;

    useEffect(() => {
        const handlePointerDown = (event) => {
            if (
                containerRef.current
                && !containerRef.current.contains(event.target)
            ) {
                setActiveIndex(null);
                setHoveredIndex(null);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
        };
    }, []);

    const toggleBucket = (index) => {
        if (canInteract) setActiveIndex((current) => current === index ? null : index);
    };

    const formatVisibleMoney = (value) =>
        valuesVisible ? formatCents(value) : '••••';

    const activeBalance = activeItem
        ? activeItem.income - activeItem.expense
        : 0;

    return (
        <div
            className="movements-flow-chart"
            ref={containerRef}
            data-layout-ready={layout.ready}
            data-layout-width={layout.width}
            data-layout-height={layout.height}
        >
            <svg
                className="movements-flow-chart__svg"
                width={width}
                height={height}
                preserveAspectRatio="xMidYMid meet"
                viewBox={`0 0 ${width} ${height}`}
                role="img"
                aria-label={hasSeries
                    ? `Gráfico interativo de ${visibleSeries.map((key) => SERIES[key].label.toLowerCase()).join(' e ')} do período`
                    : 'Gráfico do período sem séries disponíveis'}
            >
                {[0.25, 0.5, 0.75].map((ratio) => (
                    <line
                        className="movements-flow-chart__grid"
                        key={ratio}
                        x1={padding.left}
                        x2={width - padding.right}
                        y1={padding.top + chartHeight * ratio}
                        y2={padding.top + chartHeight * ratio}
                    />
                ))}

                {activeItem && (
                    <line
                        className="movements-flow-chart__guide"
                        x1={activeX}
                        x2={activeX}
                        y1={padding.top}
                        y2={height - padding.bottom}
                    />
                )}

                {geometry.valid && visibleSeries.filter(key => geometry.series[key]?.valid).map((key) => (
                    <path
                        key={`${accountId}|${periodKey}|${layout.ready}|${key}|${geometry.paths[key]}`}
                        data-flow-series={key}
                        fill="none"
                        stroke={FLOW_COLORS[key]}
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeOpacity="1"
                        opacity="1"
                        strokeDasharray="none"
                        strokeDashoffset="0"
                        className={`movements-flow-chart__line movements-flow-chart__line--${key}`}
                        d={geometry.paths[key] || ''}
                    />
                ))}

                {geometry.valid && displayData.map((item, index) => {
                    const bucketWidth =
                        displayData.length <= 1
                            ? width - padding.left - padding.right
                            : (width - padding.left - padding.right)
                                / (displayData.length - 1);

                    const hitX = Math.max(
                        padding.left,
                        getX(index) - bucketWidth / 2
                    );

                    const isActive = canInteract && selectedIndex === index;

                    return (
                        <g
                            className={`movements-flow-chart__bucket${isActive ? ' is-active' : ''}`}
                            key={`${item.label}-${index}`}
                            role={hasSeries ? 'button' : undefined}
                            tabIndex={hasSeries ? 0 : undefined}
                            aria-pressed={hasSeries ? isActive : undefined}
                            aria-label={hasSeries
                                ? `${item.rangeLabel || item.label}. ${valuesVisible
                                    ? visibleSeries.map((key) => `${SERIES[key].label} ${formatCents(item[key])}.`).join(' ')
                                    : 'Valores ocultos.'}`
                                : undefined}
                            onPointerEnter={(event) => {
                                if (hasSeries && event.pointerType === 'mouse') setHoveredIndex(index);
                            }}
                            onPointerLeave={(event) => {
                                if (event.pointerType === 'mouse') setHoveredIndex(null);
                            }}
                            onClick={() => toggleBucket(index)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    toggleBucket(index);
                                }

                                if (event.key === 'Escape') {
                                    setActiveIndex(null);
                                    setHoveredIndex(null);
                                }
                            }}
                        >
                            {hasSeries && <rect
                                className="movements-flow-chart__hit-area"
                                x={hitX}
                                y={padding.top}
                                width={Math.min(
                                    bucketWidth,
                                    width - padding.right - hitX
                                )}
                                height={chartHeight}
                            />}

                            {visibleSeries.map((key) => (
                                <circle
                                    key={key}
                                    data-flow-series={key}
                                    fill={FLOW_COLORS[key]}
                                    stroke="var(--nf-surface, #FFFFFF)"
                                    strokeWidth="2"
                                    opacity="1"
                                    className={`movements-flow-chart__point movements-flow-chart__point--${key}`}
                                    cx={getX(index)}
                                    cy={getY(item[key])}
                                    r={isActive ? '5.5' : '3.5'}
                                />
                            ))}
                            {axisLabels[index] && <text
                                className="movements-flow-chart__label"
                                x={getX(index)}
                                y={height - 4}
                                textAnchor={data.length > 1 && index === 0 ? 'start' : data.length > 1 && index === data.length - 1 ? 'end' : 'middle'}
                                style={{ fontSize: 11 * width / Math.max(1, containerWidth) }}
                                aria-hidden="true"
                            >
                                {axisLabels[index]}
                            </text>}
                        </g>
                    );
                })}
            </svg>

            {!geometry.valid && <span className="movements-flow-chart__error" role="alert">
                Não foi possível desenhar o gráfico com os valores recebidos.
            </span>}

            {activeItem && (
                <div
                    className={`movements-flow-tooltip movements-flow-tooltip--${getAlignment(selectedIndex, displayData.length)}`}
                    style={{ '--tooltip-x': `${activePercent}%` }}
                    role="status"
                    aria-live="polite"
                >
                    <div className="movements-flow-tooltip__header">
                        <strong>{activeItem.rangeLabel || activeItem.label}</strong>
                        {Number.isFinite(activeItem.count) && (
                            <span>
                                {activeItem.count}{' '}
                                {activeItem.count === 1
                                    ? 'movimentação'
                                    : 'movimentações'}
                            </span>
                        )}
                    </div>

                    {visibleSeries.map((key) => {
                        const { label, Icon } = SERIES[key];
                        return (
                            <div key={key} className={`movements-flow-tooltip__row is-${key}`}>
                                <Icon aria-hidden="true" />
                                <span>{label}</span>
                                <strong>{formatVisibleMoney(activeItem[key])}</strong>
                            </div>
                        );
                    })}

                    {comparesTypes && <div className="movements-flow-tooltip__balance">
                        <span>Saldo do intervalo</span>
                        <strong>{formatVisibleMoney(activeBalance)}</strong>
                    </div>}
                </div>
            )}
        </div>
    );
};

export default FinancialFlowChart;